import React from 'react';

import '../style.css';
import { Pie } from '@vx/shape';
import { Group } from '@vx/group';
import { Text } from '@vx/text';
import CONF from '../Conf';

export default function WeeklySummaryCard({
  now,
  secondsScoreWasGood,
  secondsScoreWasFair,
  secondsScoreWasPoor,
  secondsScoreWasUnknown,
  abTest,
}) {
  const months = ['Jan', 'Feb', 'March', 'April', 'May', 'June', 'July', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const endDate = new Date(now);
  let startDate = new Date(now);
  startDate.setDate(startDate.getDate() - 7);

  const radius = 50;

  let data = [
    {
      quality: 'good',
      color: 'var(--green)',
      seconds: secondsScoreWasGood,
      roundedPercents: Math.ceil((100 * secondsScoreWasGood) / (60 * 60 * 24 * 7)),
      roundedHours: Math.ceil(secondsScoreWasGood / (60 * 60)),
    },
    {
      quality: 'fair',
      color: 'var(--amber)',
      seconds: secondsScoreWasFair,
      roundedPercents: Math.ceil((100 * secondsScoreWasFair) / (60 * 60 * 24 * 7)),
      roundedHours: Math.ceil(secondsScoreWasFair / (60 * 60)),
    },
    {
      quality: 'poor',
      color: 'var(--red)',
      seconds: secondsScoreWasPoor,
      roundedPercents: Math.ceil((100 * secondsScoreWasPoor) / (60 * 60 * 24 * 7)),
      roundedHours: Math.ceil(secondsScoreWasPoor / (60 * 60)),
    },
    {
      quality: 'no data',
      color: 'var(--grime)',
      seconds: secondsScoreWasUnknown,
      roundedPercents: Math.ceil((100 * secondsScoreWasUnknown) / (60 * 60 * 24 * 7)),
      roundedHours: Math.ceil(secondsScoreWasUnknown / (60 * 60)),
    },
  ];

  data.sort((a, b) => b.roundedPercents - a.roundedPercents);

  const sumOfExcess = data.map((i) => i.roundedPercents).reduce((a, b) => a + b, 0) - 100;

  if (data.length !== 0) {
    for (let i = 0; i < sumOfExcess; i++) {
      data[i].roundedPercents -= 1;
    }
  }

  const roundedPercents = data.filter((i) => i.roundedPercents !== 0).map((i) => i.roundedPercents);
  const roundedGoodPercent = (data.find((i) => i.quality === 'good') || { roundedValue: 0 }).roundedPercents;
  const colors = data.filter((i) => i.roundedPercents !== 0).map((i) => i.color);

  data.sort((a, b) => b.roundedHours - a.roundedHours);

  const sumOfExcessForHours = data.map((i) => i.value).reduce((a, b) => a + b, 0) - 168;

  for (let i = 0; i < sumOfExcessForHours; i++) {
    data[i].roundedHours -= 1;
  }

  const goodRoundedHours = data.find((i) => i.quality === 'good').roundedHours;
  const fairRoundedHours = data.find((i) => i.quality === 'fair').roundedHours;
  const poorRoundedHours = data.find((i) => i.quality === 'poor').roundedHours;
  const nodataRoundedHours = data.find((i) => i.quality === 'no data').roundedHours;

  const heading = <p>{CONF.text['Your room had good air for ()% of last week'](roundedGoodPercent)}</p>;

  return (
    <article className='WeeklySummary Card' id={abTest}>
      <div>
        <header>
          {CONF.iconFromMetric('SCORE')}
          <h1>{CONF.text['7 Day Summary']}</h1>
        </header>
        {heading}
      </div>
      <div>
        <section>
          <h1>
            {months[startDate.getMonth()] +
              ' ' +
              startDate.getDate() +
              ' - ' +
              months[endDate.getMonth()] +
              ' ' +
              endDate.getDate()}
          </h1>
          <div>
            <svg viewBox='0 0 100 100' preserveAspectRatio='xMinYMid meet'>
              {roundedPercents.length === 1 && roundedPercents[0] === 100 ? (
                <Group top={50} left={radius}>
                  <circle r={radius} fill={colors[0]} />
                  <Text
                    x={0}
                    y={0}
                    textAnchor='middle'
                    verticalAnchor='middle'
                    fill='white'
                    style={{
                      fontSize: '8pt',
                    }}>
                    {`100%`}
                  </Text>
                </Group>
              ) : (
                <Group top={50} left={radius}>
                  <Pie
                    data={roundedPercents.map((i) => i)}
                    outerRadius={radius}
                    fill={(d) => colors[d.index]}
                    padAngle={0.02}
                    innerRadius={0.5}
                    centroid={(centroid, arc) => {
                      if (arc.index === roundedPercents.length - 1 && roundedPercents.length >= 1) {
                        return null;
                      } else {
                        const [x, y] = centroid;
                        return (
                          <Text
                            x={x}
                            y={y}
                            textAnchor='middle'
                            fill='white'
                            style={{
                              fontSize: '8pt',
                            }}>
                            {`${arc.data}%`}
                          </Text>
                        );
                      }
                    }}
                  />
                </Group>
              )}
            </svg>
            <div>
              <table>
                <tbody>
                  <tr>
                    <td>
                      <svg viewBox='0 0 100 100'>
                        <circle fill={CONF.verdict.GOOD.color} cx='50' cy='50' r='50' />
                      </svg>
                    </td>
                    <td>{CONF.text['Good']()}</td>
                    <td>{goodRoundedHours}</td>
                    <td>{CONF.text['H']()}</td>
                  </tr>
                  <tr>
                    <td>
                      <svg viewBox='0 0 100 100'>
                        <circle fill={CONF.verdict.FAIR.color} cx='50' cy='50' r='50' />
                      </svg>
                    </td>
                    <td>{CONF.text['Fair']()}</td>
                    <td>{fairRoundedHours}</td>
                    <td>{CONF.text['H']()}</td>
                  </tr>
                  <tr>
                    <td>
                      <svg viewBox='0 0 100 100'>
                        <circle fill={CONF.verdict.POOR.color} cx='50' cy='50' r='50' />
                      </svg>
                    </td>
                    <td>{CONF.text['Poor']()}</td>
                    <td>{poorRoundedHours}</td>
                    <td>{CONF.text['H']()}</td>
                  </tr>
                  <tr>
                    <td>
                      <svg viewBox='0 0 100 100'>
                        <circle fill={'var(--grime)'} cx='50' cy='50' r='50' />
                      </svg>
                    </td>
                    <td>{CONF.text['Unknown']()}</td>
                    <td>{nodataRoundedHours}</td>
                    <td>{CONF.text['H']()}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </article>
  );
}
